import SharedInfoPanel from "../../shared/InfoPanel"
import "../styles/InfoPanel.css"

const TABS = [
  { label: "Overview", key: "info" },
  { label: "Operations", key: "operations" },
  { label: "Applications", key: "applications" },
]

const BASIC_INFO = {
  description:
    "A linked list is a linear data structure where elements are stored in nodes. Each node contains data and a reference (or link) to the next node in the sequence. Unlike arrays, linked lists don't require contiguous memory allocation.",
  operations: [
    { name: "Insert", description: "Adds a node to the list", complexity: "O(1) to O(n)" },
    { name: "Delete", description: "Removes a node from the list", complexity: "O(1) to O(n)" },
    { name: "Search", description: "Finds a node with a specific value", complexity: "O(n)" },
    { name: "Traverse", description: "Visits each node in the list", complexity: "O(n)" },
  ],
  applications: [
    "Implementation of stacks and queues",
    "Dynamic memory allocation",
    "Maintaining directory of names",
    "Performing arithmetic operations on long integers",
  ],
  analogy:
    "A chain where each link connects to the next link. To reach a specific link, you must start at the beginning and follow the connections.",
  implementationDetail:
    "In a basic linked list, each node contains data and a reference to the next node. The first node is called the head, and the last node points to null. To access any element, you must start at the head and follow the references.",
  exampleTitle: "Undo Functionality",
  exampleText:
    "Text editors use linked lists to implement undo functionality. Each edit operation is stored as a node, and when you undo, you move to the previous node in the list.",
}

const SINGLY_INFO = {
  description:
    "A singly linked list is a linear data structure where each element (node) contains data and a reference to the next node. It allows for efficient insertion and deletion operations.",
  operations: [
    { name: "Insert at Beginning", description: "Adds a node at the start of the list", complexity: "O(1)" },
    { name: "Insert at End", description: "Adds a node at the end of the list", complexity: "O(n)" },
    { name: "Delete", description: "Removes a node from the list", complexity: "O(n)" },
    { name: "Search", description: "Finds a node with a specific value", complexity: "O(n)" },
  ],
  applications: [
    "Implementation of stacks and queues",
    "Symbol tables in compilers",
    "Dynamic memory allocation",
    "Music playlists (forward navigation)",
  ],
  analogy:
    "A train where each car only connects to the next car. You can only move forward through the train, from one car to the next.",
  implementationDetail:
    "In a singly linked list, insertion at the beginning is O(1) because we simply need to update the new node's next pointer and change the head. Insertion at the end requires traversing the entire list to find the last node, making it O(n).",
  exampleTitle: "Music Playlist (Forward Only)",
  exampleText:
    "A basic music player that only allows you to move to the next song uses a singly linked list structure. Each song points to the next song in the playlist, but you can't go back to previous songs.",
}

const DOUBLY_INFO = {
  description:
    "A doubly linked list is a linked list where each node contains references to both the next and previous nodes. This allows traversal in both directions.",
  operations: [
    { name: "Insert at Beginning", description: "Adds a node at the start of the list", complexity: "O(1)" },
    { name: "Insert at End", description: "Adds a node at the end of the list", complexity: "O(1)" },
    { name: "Delete", description: "Removes a node from the list", complexity: "O(1)" },
    { name: "Search", description: "Finds a node with a specific value", complexity: "O(n)" },
  ],
  applications: [
    "Browser's forward and backward navigation",
    "Music players with previous/next functionality",
    "Text editors (undo/redo operations)",
    "LRU (Least Recently Used) Cache",
  ],
  analogy:
    "A photo album where you can flip pages both forward and backward. Each page is connected to both the previous and next pages.",
  implementationDetail:
    "Doubly linked lists allow O(1) operations for both insertion and deletion when the position is known, as we can directly access both previous and next nodes. This makes operations more efficient compared to singly linked lists.",
  exampleTitle: "Browser History",
  exampleText:
    "Web browsers use a structure similar to doubly linked lists to implement the back and forward buttons. Each page you visit is like a node, connected to both the previous and next pages you've visited.",
}

const CIRCULAR_INFO = {
  description:
    "A circular linked list is a variation where the last node points back to the first node, creating a circle. This allows continuous traversal through the list.",
  operations: [
    { name: "Insert", description: "Adds a node to the list", complexity: "O(1) or O(n)" },
    { name: "Delete", description: "Removes a node from the list", complexity: "O(n)" },
    { name: "Traverse", description: "Visits each node in the list", complexity: "O(n)" },
  ],
  applications: [
    "Round-robin scheduling in operating systems",
    "Circular queues in memory management",
    "Turn-based multiplayer games",
    "Continuous playlist looping",
  ],
  analogy:
    "A Ferris wheel where seats move in a continuous circle. After the last seat, you return to the first seat.",
  implementationDetail:
    "Circular linked lists are useful when you need to repeatedly traverse the list. The last node points back to the first, creating a circle that allows continuous traversal without reaching an end.",
  exampleTitle: "Round-Robin Scheduling",
  exampleText:
    "Operating systems use circular linked lists for round-robin scheduling, where each process gets a fixed time slice in a cyclic order. After the last process, the CPU returns to the first process, creating a continuous cycle.",
}

const INFO_MAP = {
  basic: BASIC_INFO,
  singly: SINGLY_INFO,
  doubly: DOUBLY_INFO,
  circular: CIRCULAR_INFO,
}

const TITLE_MAP = {
  basic: "Basic Linked List",
  singly: "Singly Linked List",
  doubly: "Doubly Linked List",
  circular: "Circular Linked List",
}

function InfoPanel({ type }) {
  const info = INFO_MAP[type] || BASIC_INFO

  const renderTab = (activeTab) => {
    if (activeTab === "info") {
      return (
        <div className="info-section">
          <h3>
            What is a{" "}
            {type === "basic" ? "" : type === "singly" ? "Singly " : type === "doubly" ? "Doubly " : "Circular "}
            Linked List?
          </h3>
          <p>{info.description}</p>

          <div className="analogy-box">
            <h4>Real-Life Analogy</h4>
            <p>{info.analogy}</p>
          </div>

          <div className="visual-explanation">
            {type === "basic" && (
              <div className="list-diagram basic">
                <div className="llnode"><div className="llnode-data">A</div><div className="llnode-pointer">→</div></div>
                <div className="llnode"><div className="llnode-data">B</div><div className="llnode-pointer">→</div></div>
                <div className="llnode"><div className="llnode-data">C</div><div className="llnode-pointer">→</div></div>
                <div className="llnode"><div className="llnode-data">D</div><div className="llnode-pointer">⊘</div></div>
              </div>
            )}
            {type === "singly" && (
              <div className="list-diagram singly">
                <div className="llnode"><div className="llnode-data">A</div><div className="llnode-pointer">→</div></div>
                <div className="llnode"><div className="llnode-data">B</div><div className="llnode-pointer">→</div></div>
                <div className="llnode"><div className="llnode-data">C</div><div className="llnode-pointer">→</div></div>
                <div className="llnode"><div className="llnode-data">D</div><div className="llnode-pointer">⊘</div></div>
              </div>
            )}
            {type === "doubly" && (
              <div className="list-diagram doubly">
                <div className="llnode"><div className="llnode-pointer left">⊘</div><div className="llnode-data">A</div><div className="llnode-pointer right">→</div></div>
                <div className="llnode"><div className="llnode-pointer left">←</div><div className="llnode-data">B</div><div className="llnode-pointer right">→</div></div>
                <div className="llnode"><div className="llnode-pointer left">←</div><div className="llnode-data">C</div><div className="llnode-pointer right">→</div></div>
                <div className="llnode"><div className="llnode-pointer left">←</div><div className="llnode-data">D</div><div className="llnode-pointer right">⊘</div></div>
              </div>
            )}
            {type === "circular" && (
              <div className="list-diagram circular">
                <div className="llnode"><div className="llnode-data">A</div><div className="llnode-pointer">→</div></div>
                <div className="llnode"><div className="llnode-data">B</div><div className="llnode-pointer">→</div></div>
                <div className="llnode"><div className="llnode-data">C</div><div className="llnode-pointer">→</div></div>
                <div className="llnode"><div className="llnode-data">D</div><div className="llnode-pointer circular-end">↩</div></div>
              </div>
            )}
          </div>
        </div>
      )
    }

    if (activeTab === "operations") {
      return (
        <div className="operations-section">
          <h3>Common Operations</h3>
          <table className="operations-table">
            <thead>
              <tr>
                <th>Operation</th>
                <th>Description</th>
                <th>Time Complexity</th>
              </tr>
            </thead>
            <tbody>
              {info.operations.map((op, index) => (
                <tr key={index}>
                  <td>{op.name}</td>
                  <td>{op.description}</td>
                  <td>{op.complexity}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="operation-details">
            <h4>Implementation Details</h4>
            <p>{info.implementationDetail}</p>
          </div>
        </div>
      )
    }

    if (activeTab === "applications") {
      return (
        <div className="applications-section">
          <h3>Real-world Applications</h3>
          <ul className="applications-list">
            {info.applications.map((app, index) => (
              <li key={index} className="application-item">
                <div className="application-icon"></div>
                <div className="application-text">{app}</div>
              </li>
            ))}
          </ul>
          <div className="application-example">
            <h4>Example: {info.exampleTitle}</h4>
            <p>{info.exampleText}</p>
          </div>
        </div>
      )
    }
  }

  return (
    <SharedInfoPanel
      title={TITLE_MAP[type] || "Linked List"}
      tabs={TABS}
      renderTab={renderTab}
    />
  )
}

export default InfoPanel
